import type React from "react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import {
  DEFAULT_COLUMNS,
  DepotTable,
  OPTIONAL_COLUMNS,
} from "../components/investments/DepotTable";
import { InvestmentsStatsStrip } from "../components/investments/InvestmentsStatsStrip";
import { TopPositionsList } from "../components/investments/TopPositionsList";
import { CardListDepot } from "../components/mobile/CardListDepot";
import { useBackfillQuotes, useInvestments, useRefreshAll } from "../hooks/investments";
import { useInvestmentsUrlState } from "../hooks/useInvestmentsUrlState";
import { useIsMobile } from "../hooks/useIsMobile";
import type { InvestmentListItem } from "../lib/investments";
import { mapInvestmentsError } from "../lib/investments-errors";

const ALL_COLUMNS = [...DEFAULT_COLUMNS, ...OPTIONAL_COLUMNS] as const;

export function InvestmentsList() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const { sort, order, setSort } = useInvestmentsUrlState();

  const list = useInvestments({ active: true, period: "ytd" });
  const refreshAll = useRefreshAll();
  const backfill = useBackfillQuotes();
  const [backfillResult, setBackfillResult] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  async function handleBackfill(e: React.MouseEvent<HTMLButtonElement>) {
    setBackfillResult(null);
    const mode: "incremental" | "full" = e.altKey ? "full" : "incremental";
    try {
      const r = await backfill.mutateAsync({ mode });
      if (r.mode === "workflow") {
        setBackfillResult(r.message);
      } else {
        setBackfillResult(
          `${mode === "full" ? "Vollstaendig: " : ""}${r.ok} OK · ${r.skipped} ueberspr · ${r.failed} Fehler (gesamt ${r.total})`,
        );
      }
    } catch (err) {
      setBackfillResult(
        err instanceof Error ? err.message : "Backfill fehlgeschlagen — siehe Logs",
      );
    }
  }

  async function handleRefreshAll() {
    setRefreshError(null);
    setBackfillResult(null);
    try {
      const r = await refreshAll.mutateAsync();
      if (r.mode === "workflow") {
        setBackfillResult(r.message);
      }
    } catch (err) {
      const code =
        err &&
        typeof err === "object" &&
        "code" in err &&
        typeof (err as { code: unknown }).code === "string"
          ? (err as { code: string }).code
          : undefined;
      setRefreshError(code ? t(mapInvestmentsError(code)) : t("errors.unknown"));
    }
  }

  const allInvestments = list.data?.investments ?? [];

  const isClosed = useCallback((i: InvestmentListItem): boolean => {
    const q = Number.parseFloat(i.quantity);
    return Number.isFinite(q) && Math.abs(q) < 1e-8;
  }, []);

  const closedCount = allInvestments.filter(isClosed).length;
  const showClosed = searchParams.get("closed") === "1";
  const items = useMemo(
    () => (showClosed ? allInvestments : allInvestments.filter((i) => !isClosed(i))),
    [allInvestments, showClosed, isClosed],
  );

  function toggleShowClosed() {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      if (showClosed) sp.delete("closed");
      else sp.set("closed", "1");
      return sp;
    });
  }

  if (list.isLoading) {
    return (
      <div className="fv-card p-10 text-center" style={{ color: "var(--muted)" }}>
        …
      </div>
    );
  }

  const isEmpty = items.length === 0;

  return (
    <section className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div
            className="text-xs font-semibold uppercase tracking-wider mb-1"
            style={{ color: "var(--muted)" }}
          >
            {t("nav.group_assets")}
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{t("investments.list.title")}</h1>
          <div className="text-sm mt-1" style={{ color: "var(--muted)" }}>
            {allInvestments.length}{" "}
            {t("investments.positions_plural", { defaultValue: "Positionen" })}
            {closedCount > 0 &&
              ` · ${closedCount} ${t("investments.list.closed_label", { defaultValue: "geschlossen" })}`}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="px-3 py-2 rounded-lg border text-sm font-medium disabled:opacity-50"
            style={{ borderColor: "var(--border)", background: "var(--card)" }}
            onClick={handleRefreshAll}
            disabled={refreshAll.isPending}
          >
            {refreshAll.isPending ? "…" : t("investments.list.refresh_all")}
          </button>
          <Link
            to="/investments/new"
            className="px-3 py-2 rounded-lg text-white text-sm font-medium"
            style={{ background: "var(--accent)" }}
          >
            + {t("investments.list.new")}
          </Link>
          <details className="relative">
            <summary
              className="list-none px-3 py-2 rounded-lg cursor-pointer select-none text-sm font-medium hover:bg-[var(--row-hover)]"
              style={{
                border: "1px solid var(--border)",
                background: "var(--card)",
                color: "var(--muted)",
              }}
              title={t("investments.list.more_actions", { defaultValue: "Weitere Aktionen" })}
            >
              ⋮
            </summary>
            <div
              className="absolute right-0 top-full mt-1 z-10 rounded-lg overflow-hidden shadow-md min-w-[240px] py-1 text-sm"
              style={{
                background: "var(--card)",
                border: "1px solid var(--border)",
              }}
            >
              <button
                type="button"
                className="block w-full text-left px-3 py-2 hover:bg-[var(--row-hover)] disabled:opacity-50"
                onClick={handleBackfill}
                disabled={backfill.isPending}
                title={t("investments.list.backfill_title")}
              >
                {backfill.isPending
                  ? `${t("investments.list.backfill")} …`
                  : t("investments.list.backfill")}
              </button>
              {closedCount > 0 && (
                <button
                  type="button"
                  className="block w-full text-left px-3 py-2 hover:bg-[var(--row-hover)]"
                  onClick={toggleShowClosed}
                >
                  {showClosed
                    ? t("investments.list.hide_closed")
                    : t("investments.list.show_closed")}
                  <span className="ml-1 text-xs" style={{ color: "var(--muted)" }}>
                    ({closedCount})
                  </span>
                </button>
              )}
            </div>
          </details>
        </div>
      </div>

      {refreshError && (
        <div
          className="fv-card p-3 text-sm"
          style={{ background: "var(--neg-soft)", color: "var(--neg)" }}
        >
          {refreshError}
        </div>
      )}
      {backfillResult && (
        <div
          className="fv-card p-3 text-sm"
          style={{ background: "var(--border-soft)", color: "var(--text)" }}
        >
          {backfillResult}
        </div>
      )}

      {isEmpty ? (
        <div className="fv-card p-10 text-center">
          <p style={{ color: "var(--text)" }} className="mb-3">
            {t("investments.list.empty")}
          </p>
          <Link
            to="/investments/new"
            className="inline-block px-3 py-2 rounded-lg text-white text-sm font-medium"
            style={{ background: "var(--accent)" }}
          >
            {t("investments.list.empty_cta")}
          </Link>
        </div>
      ) : (
        <>
          <InvestmentsStatsStrip items={items} />

          <TopPositionsList items={items} />

          {isMobile ? (
            <CardListDepot items={items} />
          ) : (
            <div className="fv-card overflow-hidden">
              <DepotTable
                items={items}
                visibleColumns={[...ALL_COLUMNS]}
                sort={sort}
                order={order}
                onSortChange={setSort}
              />
            </div>
          )}
        </>
      )}
    </section>
  );
}
